import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// DSO codes for reference detection
const DSO_CODES = new Set([
  '911', '916', '917', '903', '914', '905', '904', '913',
  '312', '302', '301', '303', '882', '315', '309', '310', '308', '314', '311', '307', '305', '304', '306', '888', '313',
  '413', '402', '404', '401', '886', '411', '405', '408', '407', '412', '406', '409', '410', '883',
  '280', '283', '281', '284', '285', '286', '282',
  '217', '219', '211', '887', '220', '218', '213', '208', '212', '209', '215', '881', '210', '255', '216', '207', '205', '201', '203', '901',
  '224', '225', '221', '223', '226', '222', '230',
  '270', '271', '272', '891', '274', '273',
  '249', '247', '245', '227', '228', '246', '229', '252', '250', '243', '235', '239', '233', '231', '244', '236', '240',
  '519', '515', '517', '511', '512', '516', '518', '521',
  '559', '580', '555', '547', '885', '558', '556', '592', '560', '590', '591', '592', '549', '587', '557', '548',
  '527', '525', '509', '523', '889', '510', '522', '524', '528', '542',
  '533', '544', '539', '540', '535', '534', '536', '545', '543', '541',
  '577', '553', '563', '561', '552', '564', '583', '554', '581', '573', '565', '571', '569', '566', '890', '574', '567', '575', '570', '572',
  '531', '505', '501', '503', '884', '520', '506', '123', '111', '555', '705', '702', '701', '708', '707', '704', '703', '706',
]);

// Pattern: "1 dari 2", "2 dari 3", etc.
const PAGE_PATTERN_REGEX = /(\d+)\s+dari\s+(\d+)/gi;
// Pattern: DSO reference code like 917-G917251020143219035LQT
const REF_CODE_REGEX = /(\d{3})-G\S+/g;

/**
 * Get current date as dd-mm-yyyy string
 */
function getDateString() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Extract text from a specific page
 */
async function extractPageText(pdfDoc, pageNum) {
  try {
    const page = await pdfDoc.getPage(pageNum);
    const content = await page.getTextContent();
    return content.items.map(item => item.str).join(' ');
  } catch (e) {
    return '';
  }
}

/**
 * Auto-split PDF based on page patterns ("X dari Y") or reference codes
 */
export async function autoSplitPdf(file) {
  const arrayBuffer = await file.arrayBuffer();

  // Load with pdf.js for text extraction
  const pdfJs = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
  const totalPages = pdfJs.numPages;

  // Analyze each page
  const pageInfos = [];
  for (let i = 1; i <= totalPages; i++) {
    const text = await extractPageText(pdfJs, i);

    // Find page pattern "X dari Y"
    PAGE_PATTERN_REGEX.lastIndex = 0;
    let pagePattern = null;
    let match;
    while ((match = PAGE_PATTERN_REGEX.exec(text)) !== null) {
      const current = parseInt(match[1], 10);
      const total = parseInt(match[2], 10);
      if (current >= 1 && current <= total && total <= 100) {
        pagePattern = { current, total };
        break;
      }
    }

    // Find reference code
    REF_CODE_REGEX.lastIndex = 0;
    let refCode = null;
    while ((match = REF_CODE_REGEX.exec(text)) !== null) {
      const prefix = match[1];
      if (DSO_CODES.has(prefix)) {
        refCode = match[0];
        break;
      }
    }

    pageInfos.push({ pageNum: i, pagePattern, refCode, text });
  }

  // Determine document boundaries
  const documents = [];
  let docStart = 0;
  let currentRefCode = null;

  for (let i = 0; i < pageInfos.length; i++) {
    const info = pageInfos[i];
    const isLastPage = i === pageInfos.length - 1;

    // Update reference code for this document
    if (info.refCode && !currentRefCode) {
      currentRefCode = info.refCode;
    }

    // Check if this is the end of a document
    let isDocEnd = false;

    // Method 1: Page pattern shows last page (e.g., "2 dari 2")
    if (info.pagePattern && info.pagePattern.current === info.pagePattern.total) {
      isDocEnd = true;
    }

    // Method 2: Next page has a new reference code (different document)
    if (!isDocEnd && !isLastPage) {
      const nextInfo = pageInfos[i + 1];
      if (nextInfo.refCode && currentRefCode && nextInfo.refCode !== currentRefCode) {
        isDocEnd = true;
      }
      // Also check if next page starts with "1 dari X"
      if (nextInfo.pagePattern && nextInfo.pagePattern.current === 1) {
        isDocEnd = true;
      }
    }

    // Last page of file is always end of document
    if (isLastPage) {
      isDocEnd = true;
    }

    if (isDocEnd) {
      documents.push({
        startPage: docStart,
        endPage: i,
        refCode: currentRefCode,
      });
      docStart = i + 1;
      currentRefCode = null;
    }
  }

  // Load with pdf-lib for splitting
  const srcDoc = await PDFDocument.load(arrayBuffer);
  const stem = file.name.replace(/\.pdf$/i, '');
  const results = [];

  for (let d = 0; d < documents.length; d++) {
    const doc = documents[d];
    const pageCount = doc.endPage - doc.startPage + 1;

    if (pageCount <= 0) continue;

    const newDoc = await PDFDocument.create();
    const indices = Array.from({ length: pageCount }, (_, j) => doc.startPage + j);
    const copiedPages = await newDoc.copyPages(srcDoc, indices);
    copiedPages.forEach(page => newDoc.addPage(page));

    const pdfBytes = await newDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });

    // Generate filename with date and reference code if available
    const dateStr = getDateString();
    const fileName = doc.refCode
      ? `${doc.refCode}_${dateStr}.pdf`
      : `${stem}_part${d + 1}_${dateStr}.pdf`;

    results.push({
      fileName,
      blob,
      pages: `${doc.startPage + 1}-${doc.endPage + 1}`,
      refCode: doc.refCode,
    });
  }

  return results;
}

export async function splitPdfByPoints(file, splitPoints) {
  const arrayBuffer = await file.arrayBuffer();
  const srcDoc = await PDFDocument.load(arrayBuffer);
  const totalPages = srcDoc.getPageCount();

  const sorted = [...splitPoints].sort((a, b) => a - b);
  const ranges = [];
  let start = 0;
  for (const sp of sorted) {
    ranges.push([start, sp]);
    start = sp;
  }
  ranges.push([start, totalPages]);

  const stem = file.name.replace(/\.pdf$/i, '');
  const results = [];

  for (let i = 0; i < ranges.length; i++) {
    const [rangeStart, rangeEnd] = ranges[i];
    if (rangeStart >= rangeEnd) continue;

    const newDoc = await PDFDocument.create();
    const indices = Array.from({ length: rangeEnd - rangeStart }, (_, j) => rangeStart + j);
    const copiedPages = await newDoc.copyPages(srcDoc, indices);
    copiedPages.forEach(page => newDoc.addPage(page));

    const pdfBytes = await newDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });

    results.push({
      fileName: `${stem}_part${i + 1}_${getDateString()}.pdf`,
      blob,
      pages: `${rangeStart + 1}-${rangeEnd}`,
    });
  }

  return results;
}

